export class TradingViewCore {
    constructor(element, container, config) {
        this.el = element;
        this.sceneEl = element.sceneEl;
        this.container = container;
        this.config = config;
        this.apiManager = LiveChartAPIManager.create();;

        this.chart = null;
        this.primarySeries = null;
        this.volumeSeries = null;
        this.chartContainer = null;
        this.stockPoints = [];
        this.eventsData = [];
        this.stockDividendData = [];
        this.stockShortInterestData = [];
        this.mainTickerData = null;
        this.stockMeta = {};
        this.ticker = '';
        this.themeMode = 'Dark';
    }

    //
    // Core
    // 
    async init() {
        if (typeof LightweightCharts === 'undefined') {
            console.error('[TradingViewCore] Lightweight Charts library not found. Please include the CDN script.');
            this.showError('TradingView Lightweight Charts library not loaded');
            return;
        }

        this.stockMeta = this.config.stockData ? JSON.parse(this.config.stockData) : {};
        this.ticker = this.stockMeta.ticker || 'SPY';

        this.adjustTimespanFromDataInterval();

        await this.fetchStockData();
        if (this.config.showEvents) {
            this.eventsData = await this.apiManager.fetchTickerEvents(this.ticker);
        }

        this.applyTextRenderingWorkarounds();
        this.createChartContainer();

        if (this.chartHeaderCompanyName && this.stockMeta) {
            try {
                this.updateChartTitle();
            } catch (error) {
                console.warn('[TradingViewCore] Failed to update title after container creation:', error);
            }
        }

        this.rebuildChart();
        
        this.setupThemeListener();

        if (window.AutoRefreshScheduler && !this.autoRefreshScheduler) {
            this.autoRefreshScheduler = new window.AutoRefreshScheduler({
                name: 'TradingViewCore',
                scheduleType: 'interval',
                intervalMinutes: 5,
                onRefresh: () => this.forceRefreshData()
            });
            this.autoRefreshScheduler.start();
        }
    }
    createChartContainer() {
        this.chartContainer = document.createElement('div');
        this.chartContainer.id = `tradingview-universal-${Date.now()}`;
        this.chartContainer.className = 'chart-container';
        this.chartContainer.style.cssText = `
            // height: ${this.config.height + 20}px;
            background-color: #131722;
            border: 2px solid #131722;
            border-radius: 8px;
            font-family: -apple-system, BlinkMacSystemFont, 'Trebuchet MS', Roboto, Ubuntu, sans-serif;
            font-size: 12px;
            overflow: hidden;
            box-sizing: border-box;
            margin: 0;
            padding: 4px;
            visibility: visible;
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
            text-rendering: optimizeSpeed;
            -webkit-transform: translateZ(0);
            transform: translateZ(0);
            will-change: transform;
        `;

        const header = document.createElement('div');
        header.className = 'chart-header';
        header.style.cssText = `
            color: #000000;
            font-size: 16px;
            font-weight: bold;

            padding: 16px;
            background: #F5F5F5;
            border-radius: 0;
            text-align: left;
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
            display: flex;
            flex-direction: column;
            gap: 8px;
        `;

        const companyNameEl = document.createElement('div');
        companyNameEl.className = 'chart-header-company-name';
        companyNameEl.style.cssText = `
            font-size: 18px;
            font-weight: bold;
            color: #000000;
            line-height: 1.2;
        `;
        companyNameEl.textContent = 'Loading Chart...';

        const priceContainer = document.createElement('div');
        priceContainer.className = 'chart-header-price-container';

        const priceLeftSide = document.createElement('div');
        priceLeftSide.className = 'chart-header-price-left-side';
        const priceRightSide = document.createElement('div');
        priceRightSide.className = 'chart-header-price-right-side';

        const priceEl1 = document.createElement('div');
        priceEl1.className = 'chart-header-price';
        priceEl1.textContent = '--';

        const changeEl1 = document.createElement('div');
        changeEl1.className = 'chart-header-change';
        changeEl1.textContent = '--';

        const priceEl2 = document.createElement('div');
        priceEl2.className = 'chart-header-price';
        priceEl2.textContent = '--';

        const changeEl2 = document.createElement('div');
        changeEl2.className = 'chart-header-change';
        changeEl2.textContent = '--';

        const dateStatusEl1 = document.createElement('div');
        dateStatusEl1.className = 'chart-header-date-status';
        const dateStatusEl2 = document.createElement('div');
        dateStatusEl2.className = 'chart-header-date-status';

        const isVR = this.container === document.body;

        if (isVR) {
            const priceRow1 = document.createElement('div');
            priceRow1.className = 'chart-header-price-row';
            priceRow1.appendChild(priceEl1);
            priceRow1.appendChild(changeEl1);
            priceLeftSide.appendChild(priceRow1);
            priceLeftSide.appendChild(dateStatusEl1);

            const priceRow2 = document.createElement('div');
            priceRow2.className = 'chart-header-price-row';
            priceRow2.appendChild(priceEl2);
            priceRow2.appendChild(changeEl2);
            priceRightSide.appendChild(priceRow2);
            priceRightSide.appendChild(dateStatusEl2);

            priceContainer.appendChild(priceLeftSide);
            priceContainer.appendChild(priceRightSide);

            header.appendChild(companyNameEl);
            header.appendChild(priceContainer);
        } else {
            priceLeftSide.appendChild(priceEl1);
            priceLeftSide.appendChild(changeEl1);
            priceRightSide.appendChild(priceEl2);
            priceRightSide.appendChild(changeEl2);
            priceContainer.appendChild(priceLeftSide);
            priceContainer.appendChild(priceRightSide);

            const dateStatusContainer = document.createElement('div');
            dateStatusContainer.className = 'chart-header-date-container';
            dateStatusContainer.appendChild(dateStatusEl1);
            dateStatusContainer.appendChild(dateStatusEl2);

            header.appendChild(companyNameEl);
            header.appendChild(priceContainer);
            header.appendChild(dateStatusContainer);
        }

        const content = document.createElement('div');
        content.id = `${this.chartContainer.id}-content`;
        content.className = 'chart-content';
        content.style.cssText = `
            // width: 100%;
            // height: calc(100% - 125px);
            position: relative;
            -webkit-font-smoothing: antialiased;
        `;

        this.chartContainer.appendChild(header);
        this.chartContainer.appendChild(content);

        if (this.container) {
            this.container.appendChild(this.chartContainer);
        }

        this.chartHeader = header;
        this.chartHeaderCompanyName = companyNameEl;
        this.chartHeaderPrice1 = priceEl1;
        this.chartHeaderPrice2 = priceEl2;
        this.chartHeaderChange1 = changeEl1;
        this.chartHeaderChange2 = changeEl2;
        this.chartHeaderDateStatus1 = dateStatusEl1;
        this.chartHeaderDateStatus2 = dateStatusEl2;
        this.chartContent = content;

        this.applyThemeToContainer();
    }
    initializeLightweightChart() {
        if (!this.chartContent) return;

        try {
            const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

            let chartWidth = this.config.width - 8;
            let chartHeight = this.config.height - 115;

            // // Layout checks (mostly for desktop, VR will skip if elements don't exist)
            // const centerChartOverlay = document.getElementById('center-chart-overlay');
            // const rightWrapper = document.querySelector('.right-wrapper');
            // const isFullscreen = rightWrapper && rightWrapper.classList.contains('financials-expanded');
            //
            // if (centerChartOverlay) {
            //     const wrapper = centerChartOverlay.querySelector('.center-live-chart-wrapper');
            //     if (wrapper) {
            //         const rect = wrapper.getBoundingClientRect();
            //         const containerHeight = 380;
            //         const availableWidth = rect.width;
            //
            //         if (availableWidth >= 40) {
            //             chartWidth = Math.min(availableWidth - 32, 1200);
            //         }
            //         chartHeight = containerHeight - 150;
            //
            //         if (this.chartContainer) {
            //             this.chartContainer.style.height = '380px';
            //             this.chartContainer.style.width = `${chartWidth + 8}px`;
            //         }
            //     }
            // } else if (isFullscreen) {
            //     const rightLiveChart = document.querySelector('#right-live-chart');
            //     if (rightLiveChart) {
            //         const availableWidth = rightLiveChart.offsetWidth;
            //         chartWidth = Math.min(availableWidth - 16, 1200);
            //         chartHeight = 380 - 115;
            //
            //         if (this.chartContainer) {
            //             this.chartContainer.style.width = `${chartWidth + 8}px`;
            //         }
            //     }
            // }

            this.chart = LightweightCharts.createChart(this.chartContent, {
                width: chartWidth,
                height: chartHeight,
                mode: 0,
                layout: {
                    background: { type: 'solid', color: '#131722' },
                    textColor: '#d1d4dc',
                    fontSize: isSafari ? 10 : 12,
                    fontFamily: isSafari ? 'Arial, sans-serif' : '-apple-system, BlinkMacSystemFont, "Trebuchet MS", Roboto, Ubuntu, sans-serif'
                },
                grid: {
                    vertLines: { visible: false },
                    horzLines: { visible: false }
                },
                crosshair: {
                    mode: LightweightCharts.CrosshairMode.Hidden
                },
                rightPriceScale: {
                    borderColor: '#FF5c7b',
                    scaleMargins: { top: 0.05, bottom: 0.05 },
                    visible: true,
                    mode: LightweightCharts.PriceScaleMode.Normal,
                    autoScale: true,
                    invertScale: false,
                    alignLabels: true,
                    borderVisible: true,
                    entireTextOnly: true,
                    minimumWidth: 80,
                    textColor: '#d1d4dc',
                    fontSize: 12,
                    fontFamily: isSafari ? 'Arial, sans-serif' : 'Trebuchet MS, Roboto, Ubuntu, sans-serif',
                    ticksVisible: true,
                    priceFormat: { type: 'price', precision: 2, minMove: 0.01 }
                },
                leftPriceScale: {
                    visible: false,
                    borderColor: '#FF5c7b',
                    scaleMargins: { top: 0.05, bottom: 0.05 },
                    mode: LightweightCharts.PriceScaleMode.Normal,
                    autoScale: true,
                    invertScale: false,
                    alignLabels: false,
                    borderVisible: true,
                    entireTextOnly: true,
                    minimumWidth: 80,
                    textColor: '#d1d4dc',
                    fontSize: 12,
                    fontFamily: isSafari ? 'Arial, sans-serif' : 'Trebuchet MS, Roboto, Ubuntu, sans-serif',
                    ticksVisible: true,
                    priceFormat: { type: 'price', precision: 2, minMove: 0.01 },
                },
                timeScale: {
                    visible: true,
                    borderColor: '#FF5c7b',
                    timeVisible: true,
                    rightOffset: 12,
                    barSpacing: 1,
                    minBarSpacing: 0.5,
                    fixLeftEdge: true,
                    fixRightEdge: true,
                    lockVisibleTimeRangeOnResize: true,
                    rightBarStaysOnScroll: true,
                    borderVisible: true,
                    ticksVisible: true,
                    tickMarkFormatter: (time, tickMarkType, locale) => {
                        if (tickMarkType === 3 || tickMarkType === 4) {
                            const date = new Date(time * 1000);
                            const hours = date.getUTCHours();
                            const minutes = date.getUTCMinutes();
                            const ampm = hours >= 12 ? 'PM' : 'AM';
                            const hours12 = hours % 12 || 12;
                            const minutesStr = minutes < 10 ? '0' + minutes : minutes;
                            return `${hours12}:${minutesStr} ${ampm}`;
                        }
                        return null;
                    },
                },
                handleScroll: { mouseWheel: true, pressedMouseMove: true },
                handleScale: { axisPressedMouseMove: true, mouseWheel: true, pinch: true },
                devicePixelRatio: isSafari ? 1 : (window.devicePixelRatio || 1)
            });

            const clampedVolumeHeight = Math.min(Math.max(this.config.volumeHeight ?? 0.30, 0.10), 0.50);
            const clampedPaneGap = Math.min(Math.max(this.config.paneGap ?? 0.04, 0.00), 0.5);
            const volumeTop = 1 - clampedVolumeHeight;
            const primaryBottom = Math.min(1, clampedVolumeHeight + clampedPaneGap);

            if (this.config.type === 'mountain') {
                this.primarySeries = this.chart.addSeries(LightweightCharts.AreaSeries, {
                    lineColor: '#651fff',
                    topColor: 'rgba(101, 31, 255, 0.35)',
                    bottomColor: 'rgba(101, 31, 255, 0.00)',
                    priceLineColor: '#651fff',
                    priceLineStyle: LightweightCharts.LineStyle.LargeDashed,
                    priceLineWidth: 2,
                    lineWidth: 2,
                });
            } else if (this.config.type === 'line') {
                this.primarySeries = this.chart.addSeries(LightweightCharts.LineSeries, {
                    color: '#651fff',
                    lineWidth: 2,
                    priceLineColor: '#651fff',
                    priceLineStyle: LightweightCharts.LineStyle.LargeDashed,
                    priceLineWidth: 2,
                });
            } else {
                this.primarySeries = this.chart.addSeries(LightweightCharts.CandlestickSeries, {
                    upColor: '#26a69a',
                    downColor: '#ef5350',
                    borderDownColor: '#ef5350',
                    borderUpColor: '#26a69a',
                    wickDownColor: '#ef5350',
                    wickUpColor: '#26a69a',
                    priceLineStyle: LightweightCharts.LineStyle.LargeDashed,
                    priceLineWidth: 2,
                });
            }

            this.volumeSeries = this.chart.addSeries(LightweightCharts.HistogramSeries, {
                color: '#ef5350',
                priceFormat: { type: 'volume' },
                priceScaleId: '',
                scaleMargins: { top: volumeTop, bottom: 0 },
                priceLineStyle: LightweightCharts.LineStyle.LargeDashed,
                priceLineWidth: 2,
            });
            this.volumeSeries.priceScale().applyOptions({
                scaleMargins: { top: volumeTop, bottom: 0 }
            });

            if (this.primarySeries && typeof this.primarySeries.priceScale === 'function') {
                this.primarySeries.priceScale().applyOptions({
                    mode: LightweightCharts.PriceScaleMode.Normal,
                    scaleMargins: { top: 0.05, bottom: primaryBottom },
                    priceFormat: { type: 'price', precision: 2, minMove: 0.01 }
                });
            }

            if (this.stockPoints && this.stockPoints.length > 0) {
                const primaryData = this.getPrimarySeriesData();
                if (primaryData) this.primarySeries.setData(primaryData);
                this.updateSecondarySeries();
            }

            this.updateChartEvents();
            this.updateLatestPriceLine();
            this.updateChartTitle();
            this.applyThemeToChart();
            this.setPointerListener();

            if (this.chart && this.stockPoints && this.stockPoints.length > 0) {
                this.chart.timeScale().fitContent();
            }

        } catch (error) {
            console.error('[TradingViewCore] Error initializing chart:', error);
            this.showError(`Chart creation error: ${error.message}`);
        }
    }
    setPointerListener() {
        const popup = document.querySelector('.live-chart-tab .right-chart-hover-popup ');
        if (!this.chart || !popup) return;

        this.chart.subscribeCrosshairMove((e) => {
            if (!e || !e.seriesData) return;
            const primary = this.primarySeries ? e.seriesData.get(this.primarySeries) : null;
            const volume = this.volumeSeries ? e.seriesData.get(this.volumeSeries) : null;

            let eventMarker = null;
            if (e.time && this.eventsData && this.eventsData.length > 0) {
                let eventTime = e.time.year ? new Date(e.time.year, e.time.month - 1, e.time.day) : new Date(e.time * 1000);
                if (eventTime && this.stockPoints && this.stockPoints.length) {
                    const delta = (new Date(this.stockPoints[this.stockPoints.length - 1].DateTime) - new Date(this.stockPoints[0].DateTime)) / this.stockPoints.length;
                    eventMarker = this.eventsData.find(ev => {
                        const d = new Date(ev.date);
                        return Math.abs(d - eventTime) <= delta * 3;
                    });
                }
            }

            if (e.time && e.sourceEvent && e.sourceEvent.clientX) {
                popup.classList.add('show');
                popup.style.left = e.sourceEvent.clientX + 10 + 'px';
                popup.style.top = e.sourceEvent.clientY + 10 + 'px';
                popup.innerHTML = '';
                if (primary) popup.innerHTML += `<div>Price: ${parseFloat(primary.value || primary.close || 0).toFixed(2)}</div>`;
                if (volume) {
                    let text = this.config.secondarySeriesType === 'dividend' ? 'Dividend' : (this.config.secondarySeriesType === 'sip' ? 'Short Interest' : 'Volume');
                    popup.innerHTML += `<div>${text}: ${this.prettifyNumber(volume.value)}</div>`;
                }
                if (eventMarker && this.config.showEvents) {
                    popup.innerHTML += `<div class="event">Event</div><div>Kind: ${eventMarker.kind}</div><div>Date: ${eventMarker.date}</div>`;
                }
            } else {
                popup.classList.remove('show');
            }
        });
    }
    
    
    // Prime Data
    async fetchStockData() {
        if (this.sceneEl) {
            this.sceneEl.emit('chart-data-fetch-started', {
                ticker: this.ticker,
                dataInterval: this.config.dataInterval
            });
        }

        try {
            const { latest, data } = await this.apiManager.fetchLiveChartData(
                this.ticker,
                this.config.dataInterval,
                this.config.secondarySeriesType
            );

            this.mainTickerData = latest;
            this.stockPoints = (data && data.stockPoints) ? data.stockPoints : [];
            
            
            if (this.chartContainer) {
                const chartContent = this.chartContainer.querySelector('.chart-content');
                if (chartContent) chartContent.classList.toggle('no-data', this.stockPoints.length === 0);
            }
            
            this.stockPoints.sort((a, b) => {
                const timeA = a.t || new Date(a.DateTime).getTime();
                const timeB = b.t || new Date(b.DateTime).getTime();
                return timeA - timeB;
            });
            this.stockDividendData = (data && data.dividends) ? data.dividends : [];
            this.stockShortInterestData = (data && data.short_interest) ? data.short_interest : [];

            this.stockPoints = this.applyCorrectTimeZone(this.stockPoints);

            let dateRange = null;
            if (this.stockPoints.length > 0) {
                const firstPoint = this.stockPoints[0];
                const lastPoint = this.stockPoints[this.stockPoints.length - 1];
                dateRange = {
                    start: firstPoint.DateTime,
                    end: lastPoint.DateTime
                };
            }

            if (this.sceneEl) {
                this.sceneEl.emit('chart-data-fetch-complete', {
                    ticker: this.ticker,
                    dataInterval: this.config.dataInterval,
                    pointCount: this.stockPoints.length,
                    dateRange: dateRange
                });
            }

            if (this.chartHeaderCompanyName) {
                this.updateChartTitle();
            }

        } catch (error) {
            console.error('Error fetching stock data:', error);
            this.stockPoints = [];

            if (this.sceneEl) {
                this.sceneEl.emit('chart-data-fetch-error', {
                    ticker: this.ticker,
                    error: error.message
                });
            }
            throw error;
        }
    }
    getPrimarySeriesData() {
        if (!this.stockPoints || this.stockPoints.length === 0) return [];

        if (this.config.type === 'candlestick') {
            return this.stockPoints.map(point => {
                let timestamp = this._getTimestamp(point);
                if (!timestamp) return null;

                let open = parseFloat(point.o || point.Open);
                let high = parseFloat(point.h || point.High);
                let low = parseFloat(point.l || point.Low);
                let close = parseFloat(point.c || point.Close);

                if (!isFinite(open) || !isFinite(high) || !isFinite(low) || !isFinite(close)) return null;

                return { time: timestamp, open, high, low, close };
            }).filter(Boolean);
        }

        return this.stockPoints.map(point => {
            let timestamp = this._getTimestamp(point);
            if (!timestamp) return null;

            let closeValue = parseFloat(point.c || point.Close);
            if (!isFinite(closeValue)) return null;

            return { time: timestamp, value: closeValue };
        }).filter(Boolean);
    }

    
    //
    // Secondary series
    //
    async updateSecondarySeries() {
        this.volumeSeries.setData([]);
        let data = [];
        if (this.config.secondarySeriesType === 'volume') data = this.convertStockDataToVolumeFormat();
        if (this.config.secondarySeriesType === 'dividend') data = this.convertStockDividendDataToVolume();
        if (this.config.secondarySeriesType === 'sip') data = this.convertStockShortInterestDataToVolume();
        this.volumeSeries.setData(data);
    }
    convertStockDataToVolumeFormat() {
        if (!this.stockPoints || this.stockPoints.length === 0) return [];
        return this.stockPoints.map(point => {
            let timestamp = this._getTimestamp(point);
            if (!timestamp) return null;
            const volumeValue = parseFloat(point.v || point.Volume);
            if (!isFinite(volumeValue)) return null;
            return { time: timestamp, value: volumeValue };
        }).filter(Boolean);
    }
    convertStockDividendDataToVolume() {
        if (!this.stockDividendData || this.stockDividendData.length === 0) return [];
        const formatted = this.stockDividendData.map(point => {
            if (!point.pay_date) return null;
            let timestamp = Math.floor(new Date(point.pay_date).getTime() / 1000);
            const value = parseFloat(point.cash_amount);
            if (!isFinite(value)) return null;
            return { time: timestamp, value };
        }).filter(Boolean);
        return formatted.sort((a, b) => new Date(a.time) - new Date(b.time));
    }
    convertStockShortInterestDataToVolume() {
        if (!this.stockShortInterestData || this.stockShortInterestData.length === 0) return [];
        const formatted = this.stockShortInterestData.map(point => {
            if (!point.settlement_date) return null;
            let timestamp = Math.floor(new Date(point.settlement_date).getTime() / 1000);
            const value = parseFloat(point.short_interest);
            if (!isFinite(value)) return null;
            return { time: timestamp, value };
        }).filter(Boolean);
        return formatted.sort((a, b) => new Date(a.time) - new Date(b.time));
    }
    
    
    // 
    // Events
    //
    updateChartEvents() {
        if (this.eventMarkers) this.eventMarkers.detach();
        if (!this.config.showEvents) return;
        if (this.config.dataInterval === '5y' || this.config.dataInterval === '5Y') return;

        const eventMarkers = [];
        if (this.eventsData && this.stockPoints && this.stockPoints.length > 0) {
            const startDate = new Date(this.stockPoints[0].DateTime);
            const endDate = new Date(this.stockPoints[this.stockPoints.length - 1].DateTime);

            this.eventsData.forEach(event => {
                const time = new Date(event.date);
                if (time < startDate || time > endDate) return;

                const isLight = document.body.classList.contains('light') || document.body.classList.contains('cb-light');
                const isCBTheme = document.body.classList.contains('cb-light') || document.body.classList.contains('cb-dark');
                const redColor = isCBTheme ? (isLight ? '#1111117F' : '#FFFFFF7F') : '#ef5350';
                const greenColor = isCBTheme ? (isLight ? '#737373' : '#4f4f4f') : '#26a69a';

                let position = 'aboveBar';
                let color = redColor;
                if (event.kind === 'dividend') color = greenColor;
                if (event.kind === 'earnings') position = 'belowBar';

                eventMarkers.push({
                    time: { year: time.getFullYear(), month: time.getMonth() + 1, day: time.getDate() },
                    position, color, shape: 'circle'
                });
            });
        }
        if (eventMarkers.length > 0) {
            this.eventMarkers = LightweightCharts.createSeriesMarkers(this.primarySeries, eventMarkers);
        }
    }
    
    
    //
    // Theme 
    //
    setupThemeListener() {
        this._onThemeChanged = (e) => {
            if (!e || !e.detail) return;
            const newTheme = e.detail.themeMode;
            if (newTheme && newTheme !== this.themeMode) this.updateTheme(newTheme);
        };
        if (this.sceneEl) this.sceneEl.addEventListener('theme-changed', this._onThemeChanged);

        this._themeObserver = new MutationObserver((mutations) => {
            for (const m of mutations) {
                if (m.attributeName === 'class') {
                    const isLight = document.body.classList.contains('light') || document.body.classList.contains('cb-light');
                    this.updateTheme(isLight ? 'Light' : 'Dark');
                }
            }
        });
        this._themeObserver.observe(document.body, { attributes: true });
    }
    updateTheme(newTheme) {
        this.themeMode = newTheme;
        setTimeout(() => {
            this.applyThemeToContainer();
            this.applyThemeToChart();
        }, 50);
    }
    applyThemeToContainer() {
        if (!this.chartContainer) return;
        this.chartContainer.style.backgroundColor = 'var(--color-bg-block)';
        this.chartContainer.style.borderColor = 'var(--color-border-popup)';
        if (this.chartHeader) {
            this.chartHeader.style.backgroundColor = 'var(--color-bg-block)';
            this.chartHeader.style.color = 'var(--color-text-primary)';
            if (this.chartHeaderCompanyName) this.chartHeaderCompanyName.style.color = 'var(--color-text-primary)';
            if (this.chartHeaderPrice1) this.chartHeaderPrice1.style.color = 'var(--color-text-primary)';
        }
    }
    applyThemeToChart() {
        if (!this.chart) return;
        const isLight = document.body.classList.contains('light') || document.body.classList.contains('cb-light');
        const isCBTheme = document.body.classList.contains('cb-light') || document.body.classList.contains('cb-dark');
        const style = getComputedStyle(document.body);
        const layoutBg = style.getPropertyValue('--color-bg-block').trim();
        const textColor = style.getPropertyValue('--color-text-primary').trim();

        const finalBg = layoutBg || (isLight ? '#ffffff' : '#131722');
        const finalTextColor = textColor || (isLight ? '#14141C' : '#d1d4dc');
        const priceLineColor = textColor || (isLight ? '#14141C' : '#d1d4dc');
        const redColor = isCBTheme ? (isLight ? '#1111117F' : '#FFFFFF7F') : '#ef5350';
        const greenColor = isCBTheme ? (isLight ? '#737373' : '#4f4f4f') : '#26a69a';

        try {
            this.chart.applyOptions({
                layout: { background: { type: 'solid', color: finalBg }, textColor: finalTextColor },
                leftPriceScale: { borderColor: redColor },
                rightPriceScale: { borderColor: redColor },
                timeScale: { borderColor: redColor }
            });
        } catch (e) {
            console.error('[TradingViewCore] Error scaling:', e);
        }

        const accentHex = isCBTheme ? (isLight ? '#111111' : '#ffffff') : '#3636E3';
        const hexToRgba = (hex, a) => {
            if (!hex.startsWith('#')) return hex;
            const r = parseInt(hex.slice(1, 3), 16), g = parseInt(hex.slice(3, 5), 16), b = parseInt(hex.slice(5, 7), 16);
            return `rgba(${r},${g},${b},${a})`;
        };

        if (this.primarySeries) {
            if (this.config.type === 'mountain') {
                this.primarySeries.applyOptions({ lineColor: accentHex, topColor: hexToRgba(accentHex, 0.35), bottomColor: hexToRgba(accentHex, 0), priceLineColor: accentHex });
            } else if (this.config.type === 'line') {
                this.primarySeries.applyOptions({ color: accentHex, priceLineColor: accentHex });
            } else {
                this.primarySeries.applyOptions({ upColor: greenColor, downColor: redColor, borderDownColor: redColor, borderUpColor: greenColor, wickDownColor: redColor, wickUpColor: greenColor });
            }
            if (this.latestPriceLine) {
                this.latestPriceLine.applyOptions({ color: priceLineColor });
            }
        }
        if (this.volumeSeries) {
            this.volumeSeries.applyOptions({ color: redColor });
        }
    }
    
    //
    // Time
    //
    adjustTimespanFromDataInterval() {
        const dataInterval = this.config.dataInterval.toLowerCase();
        let multiplier, timespan, daysBack;

        switch (dataInterval) {
            case '1d': multiplier = '30'; timespan = 'minute'; daysBack = 4; break;
            case '5d': multiplier = '4'; timespan = 'hour'; daysBack = 15; break;
            case '1m': multiplier = '1'; timespan = 'day'; daysBack = 45; break;
            case '6m': multiplier = '7'; timespan = 'day'; daysBack = 180; break;
            case 'ytd':
                multiplier = '7'; timespan = 'day';
                const now = new Date();
                daysBack = Math.floor((now - new Date(now.getFullYear(), 0, 1)) / (1000 * 60 * 60 * 24));
                break;
            case '1y': multiplier = '14'; timespan = 'day'; daysBack = 365; break;
            case '5y': multiplier = '1'; timespan = 'month'; daysBack = 365 * 5; break;
            default:
                console.warn(`[TradingViewCore] Unsupported interval: ${dataInterval}`);
                return;
        }

        this.config.multiplier = multiplier;
        this.config.timespan = timespan;
        const endDate = new Date();
        this.config.endDate = endDate.toISOString().split('T')[0];
        const startDate = new Date(endDate.getTime() - (daysBack * 24 * 60 * 60 * 1000));
        this.config.startDate = startDate.toISOString().split('T')[0];
    }
    applyCorrectTimeZone(stockPoints) {
        if (!Array.isArray(stockPoints) || stockPoints.length === 0) return [];
        const isET = !!this.config.isETTimeZone;

        return stockPoints.map(point => {
            if (!point.DateTime) return point;
            const rawDate = point.DateTime.replace(' ET', '').trim();
            const [datePart, timePart] = rawDate.split(' ');
            const [year, month, day] = datePart.split('-').map(Number);
            const [hours, minutes, seconds] = timePart.split(':').map(Number);
            const wallClockUTC = Date.UTC(year, month - 1, day, hours, minutes, seconds);

            if (isET) {
                return { ...point, t: wallClockUTC, DateTime: new Date(wallClockUTC).toISOString() };
            } else {
                const etDateString = new Date(wallClockUTC).toLocaleString('en-US', { timeZone: 'America/New_York' });
                const etDate = new Date(etDateString);
                const etOffsetMs = wallClockUTC - etDate.getTime();
                const realUTCTimestamp = wallClockUTC + etOffsetMs;
                return { ...point, t: realUTCTimestamp, DateTime: new Date(realUTCTimestamp).toISOString() };
            }
        });
    }


    // 
    // Updates  
    //
    rebuildChart() {
        if (this.chart) {
            this.chart.remove();
            this.chart = null;
        }
        this.primarySeries = null;
        this.volumeSeries = null;
        this.latestPriceLine = null;
        this.initializeLightweightChart();
    }
    updateLatestPriceLine() {
        if (!this.primarySeries || !this.mainTickerData || this.mainTickerData.period_start_close_price === undefined) return;
        
        const price = parseFloat(this.mainTickerData.period_start_close_price);
        if (isNaN(price)) return;

        this.primarySeries.applyOptions({
            autoscaleInfoProvider: (getAutoscaleInfo) => {
                const res = getAutoscaleInfo();
                if (res !== null && res.priceRange) {
                    res.priceRange.minValue = Math.min(res.priceRange.minValue, price);
                    res.priceRange.maxValue = Math.max(res.priceRange.maxValue, price);
                }
                return res;
            }
        });

        const isLight = document.body.classList.contains('light') || document.body.classList.contains('cb-light');
        const style = getComputedStyle(document.body);
        const textColor = style.getPropertyValue('--color-text-primary').trim();
        const priceLineColor = textColor || (isLight ? '#14141C' : '#d1d4dc');

        if (this.latestPriceLine) {
            this.latestPriceLine.applyOptions({ price: price, color: priceLineColor });
        } else {
            this.latestPriceLine = this.primarySeries.createPriceLine({
                price: price,
                color: priceLineColor,
                lineWidth: 2,
                lineStyle: LightweightCharts.LineStyle.LargeDashed,
            });
        }
    }
    updateChartTitle() {
        if (!this.chartHeaderCompanyName) return;

        const ticker = this.ticker || 'SPY';
        const companyName = this.stockMeta?.tickerDetails?.name || this.stockMeta?.tickerSummary?.name || ticker;
        const exchange = this.stockMeta?.tickerDetails?.primary_exchange || this.stockMeta?.tickerSummary?.primary_exchange || '';
        const exchangeAbbr = this.formatExchangeAbbreviation(exchange);
        const exchangeTicker = exchangeAbbr ? `(${exchangeAbbr}:${ticker})` : `(${ticker})`;

        this.chartHeaderCompanyName.textContent = `${companyName} ${exchangeTicker}`;

        const makeChangeLine = (changeEl, changRelValue, percentageValue) => {
            if (!changeEl) return;
            let line = '';
            if (changRelValue) line = changRelValue > 0 ? `+${changRelValue}` : `${changRelValue}`;
            if (percentageValue) line += percentageValue > 0 ? ` (+${percentageValue}%)` : ` (${percentageValue}%)`;
            changeEl.textContent = line;
            changeEl.classList.remove('positive-change', 'negative-change');
            if (changRelValue) changeEl.classList.add(changRelValue > 0 ? 'positive-change' : 'negative-change');
        };

        const formatETTime = (timeString, isET = true) => {
            if (!timeString) return '';
            const cleanString = timeString.replace(" ET", "").trim();
            const hasDate = cleanString.includes("-");
            const datePart = hasDate ? cleanString.split(" ")[0] : new Date().toISOString().split('T')[0];
            const timePart = hasDate ? cleanString.split(" ")[1] : cleanString;

            const nyFormatter = new Intl.DateTimeFormat("en-US", {
                timeZone: "America/New_York", hour12: false,
                year: "numeric", month: "numeric", day: "numeric",
                hour: "numeric", minute: "numeric", second: "numeric"
            });

            const dummyUtc = new Date(`${datePart}T${timePart}Z`);
            const nyParts = nyFormatter.formatToParts(dummyUtc);
            const nyMap = Object.fromEntries(nyParts.map(p => [p.type, p.value]));
            const nyAsUtc = new Date(Date.UTC(nyMap.year, nyMap.month - 1, nyMap.day, nyMap.hour, nyMap.minute, nyMap.second));
            const diff = dummyUtc - nyAsUtc;
            const finalDate = new Date(dummyUtc.getTime() + diff);

            const options = { hour: "numeric", minute: "2-digit", hour12: true, timeZoneName: "short" };
            if (isET) options.timeZone = "America/New_York";
            return finalDate.toLocaleTimeString("en-US", options);
        };
        
        function formatPrice(rawValue) {
            if (rawValue === null || rawValue === undefined) return '';
            const num = parseFloat(rawValue);
            if (isNaN(num)) return '';
            return `$${Number(rawValue).toFixed(2)}`;
        }
        
        if (this.mainTickerData) {
            if (this.mainTickerData.is_after_hours || this.mainTickerData.market_state === "closed") {
                this.chartHeaderPrice1.textContent = formatPrice(this.mainTickerData.close_price_today)
                this.chartHeaderPrice2.textContent = formatPrice(this.mainTickerData.latest_price)
                makeChangeLine(this.chartHeaderChange1, this.mainTickerData.price_change_today, this.mainTickerData.price_pct_change_today);
                makeChangeLine(this.chartHeaderChange2, this.mainTickerData.price_change_since_today_close, this.mainTickerData.price_pct_change_since_today_close);

                this.chartHeaderDateStatus1.textContent = this.mainTickerData.market_close_time ? `At close: ${formatETTime(this.mainTickerData.market_close_time, this.config.isETTimeZone)}` : '';
                this.chartHeaderDateStatus2.innerHTML = this.mainTickerData.as_of ? `After hours: ${formatETTime(this.mainTickerData.as_of, this.config.isETTimeZone)}` : '';
            } else {
                this.chartHeaderPrice1.textContent = formatPrice(this.mainTickerData.latest_price)
                this.chartHeaderPrice2.textContent = '';

                if (this.mainTickerData.market_state === "pre" || this.mainTickerData.market_state === "closed") {
                    makeChangeLine(this.chartHeaderChange1, '', '');
                } else {
                    makeChangeLine(this.chartHeaderChange1, this.mainTickerData.price_change_today, this.mainTickerData.price_pct_change_today);
                }
                makeChangeLine(this.chartHeaderChange2, '', '');

                const prettytime = formatETTime(this.mainTickerData.as_of, this.config.isETTimeZone);
                this.chartHeaderDateStatus2.textContent = '';
                let status = `As of: ${prettytime}. <br/> Market Open`;
                if (this.mainTickerData.market_state === "pre") status = `As of: ${prettytime}. <br/> Pre-Market`;
                if (this.mainTickerData.market_state === "closed") status = `As of: ${prettytime}. <br/> Market Closed`;
                this.chartHeaderDateStatus1.innerHTML = status;
            }
        }
    }
    async update(oldConfig) {
        if (!this.apiManager) return;

        const changedKeys = Object.keys(this.config).filter(k => this.config[k] !== oldConfig[k]);
        if (!changedKeys.length) return;

        if (changedKeys.includes('dataInterval')) this.adjustTimespanFromDataInterval();
        
        this.stockMeta = this.config.stockData ? JSON.parse(this.config.stockData) : {};
        this.ticker = this.stockMeta.ticker || 'SPY';

        if (this.config.showEvents) this.eventsData = await this.apiManager.fetchTickerEvents(this.ticker);

        const rightC = document.querySelector('#right-container');
        if (rightC) rightC.classList.add('loading');

        try {
            await this.fetchStockData();
            if (changedKeys.includes('type')) {
                this.rebuildChart();
            } else if (this.chart && this.primarySeries) {
                this.primarySeries.setData(this.getPrimarySeriesData());
                if (this.volumeSeries) this.updateSecondarySeries();
                this.updateChartEvents();
                this.updateLatestPriceLine();
                this.applyThemeToChart();
                this.chart.timeScale().fitContent();
            }
            this.updateChartTitle();
        } catch (e) {
            console.error('[TradingViewCore] Update error:', e);
        } finally {
            if (rightC) rightC.classList.remove('loading');
        }
    }
    

    async forceRefreshData() {
        if (!this.apiManager) return;
        
        const rightC = document.querySelector('#right-container');
        if (rightC) rightC.classList.add('loading');

        try {
            await this.fetchStockData();
            if (this.chart && this.primarySeries) {
                this.primarySeries.setData(this.getPrimarySeriesData());
                if (this.volumeSeries) this.updateSecondarySeries();
                this.updateChartEvents();
                this.updateLatestPriceLine();
                this.applyThemeToChart();
            }
            this.updateChartTitle();
        } catch (e) {
            console.error('[TradingViewCore] forceRefreshData error:', e);
        } finally {
            if (rightC) rightC.classList.remove('loading');
        }
    }

    remove() {
        if (this.sceneEl && this._onThemeChanged) this.sceneEl.removeEventListener('theme-changed', this._onThemeChanged);
        if (this._themeObserver) this._themeObserver.disconnect();
        if (this.autoRefreshScheduler) this.autoRefreshScheduler.stop();

        if (this.chart) {
            this.chart.remove();
            this.chart = null;
        }
        if (this.chartContainer && this.chartContainer.parentNode) {
            this.chartContainer.parentNode.removeChild(this.chartContainer);
        }
        this.chartContainer = null;
    }
    
    //
    // utils
    // 
    applyTextRenderingWorkarounds() {
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        if (isSafari && !window._canvasFillTextPatched) {
            console.log('Safari detected - applying text rendering workarounds');
            const originalFillText = CanvasRenderingContext2D.prototype.fillText;
            CanvasRenderingContext2D.prototype.fillText = function (text, x, y, maxWidth) {
                this.save();
                this.textBaseline = 'alphabetic';
                this.textAlign = this.textAlign || 'start';
                originalFillText.call(this, text, x, y, maxWidth);
                this.restore();
            };
            document.documentElement.style.setProperty('-webkit-font-smoothing', 'antialiased');
            document.documentElement.style.setProperty('-moz-osx-font-smoothing', 'grayscale');
            window._canvasFillTextPatched = true;
        }
    }

    _getTimestamp(point) {
        if (point.t) {
            return Math.floor(point.t / 1000);
        } else if (point.DateTime) {
            return Math.floor(new Date(point.DateTime).getTime() / 1000);
        }
        return null;
    }

    prettifyNumber(num) {
        if (num == null || isNaN(num)) return 'N/A';
        let n = Math.abs(num), s = '', suffixes = ['', 'K ', 'M ', 'B ', 'T '], i = 0;
        if (n === 0) return '0.00';
        while (n >= 1000 && i < suffixes.length - 1) { n /= 1000; i++; }
        return `${num < 0 ? '-' : ''}${n.toFixed(2)}${suffixes[i]}`;
    }

    formatExchangeAbbreviation(exchange) {
        if (!exchange) return '';
        const exchangeMap = {
            'NASDAQ': 'NAS', 'New York Stock Exchange': 'NYSE', 'NYSE': 'NYSE',
            'NYSE Arca': 'ARCA', 'NYSE American': 'AMEX', 'BATS': 'BATS', 'CBOE': 'CBOE', 'IEX': 'IEX'
        };
        if (exchangeMap[exchange]) return exchangeMap[exchange];

        const upper = exchange.toUpperCase();
        for (const [k, v] of Object.entries(exchangeMap)) if (k.toUpperCase() === upper) return v;
        if (upper.includes('NASDAQ')) return 'NAS';
        if (upper.includes('NYSE')) return 'NYSE';
        return exchange.substring(0, 4).toUpperCase();
    }

    showError(message) {
        if (this.chartContent) {
            this.chartContent.innerHTML = `<div style="display:flex;align-items:center;justify-content:center;height:100%;color:#ff4976;text-align:center;"><div><h3>Chart Error</h3><p>${message}</p></div></div>`;
        }
        console.error('[TradingViewCore]', message);
    }
}

window.TradingViewCore = TradingViewCore;